// instagram-auth.component.ts
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  InstagramAuthService,
  InstagramAccount,
  AuthStatus,
} from "./instagram-auth.service";

@Component({
  selector: "app-instagram-auth",
  template: `
    <div class="instagram-auth-container">
      <h2>Instagram API Authentication</h2>

      <!-- Status Display -->
      <div class="status" [ngClass]="getStatusClass()">
        {{ getStatusMessage() }}
      </div>

      <!-- Login/Logout Section -->
      <div class="auth-buttons">
        <button
          *ngIf="!authStatus.isLoggedIn"
          (click)="login()"
          [disabled]="isLoading"
          class="btn btn-primary"
        >
          {{ isLoading ? "Connecting..." : "Connect with Facebook" }}
        </button>

        <button
          *ngIf="authStatus.isLoggedIn"
          (click)="logout()"
          class="btn btn-secondary"
        >
          Logout
        </button>
      </div>

      <!-- User Info -->
      <div *ngIf="authStatus.isLoggedIn && authStatus.user" class="user-info">
        <h3>User Information</h3>
        <div class="info-card">
          <strong>Name:</strong> {{ authStatus.user.name }}<br />
          <strong>Email:</strong> {{ authStatus.user.email || "Not provided"
          }}<br />
          <strong>Facebook ID:</strong> {{ authStatus.user.id }}
        </div>
      </div>

      <!-- Get Instagram Accounts -->
      <div *ngIf="authStatus.isLoggedIn" class="instagram-section">
        <button
          (click)="getInstagramAccounts()"
          [disabled]="isLoadingInstagram"
          class="btn btn-success"
        >
          {{ isLoadingInstagram ? "Loading..." : "Get Instagram Accounts" }}
        </button>
      </div>

      <!-- Instagram Accounts Display -->
      <div *ngIf="instagramAccounts.length > 0" class="accounts-section">
        <h3>Instagram Business Accounts</h3>

        <div
          *ngFor="let account of instagramAccounts; let i = index"
          class="account-card"
        >
          <div class="account-info">
            <h4>{{ account.pageName }}</h4>
            <p>
              <strong>Instagram Account ID:</strong>
              {{ account.instagramAccountId }}
            </p>
            <p><strong>Page ID:</strong> {{ account.pageId }}</p>

            <!-- Test Button -->
            <button
              (click)="testConnection(account, i)"
              [disabled]="testingIndex === i"
              class="btn btn-outline"
            >
              {{ testingIndex === i ? "Testing..." : "Test Connection" }}
            </button>

            <!-- Test Results -->
            <div *ngIf="testResults[i]" class="test-result">
              <div *ngIf="testResults[i].success" class="success">
                ✅ Connected! Username: {{ testResults[i].data?.username }}
              </div>
              <div *ngIf="!testResults[i].success" class="error">
                ❌ Error: {{ testResults[i].error }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tokens for Firebase -->
        <div class="tokens-section">
          <h3>🔑 Tokens for Your Firebase Function</h3>
          <div class="warning">
            <strong>Important:</strong> Save these securely! You'll need them
            for your Firebase function.
          </div>

          <div
            *ngFor="let account of instagramAccounts; let i = index"
            class="token-card"
          >
            <h4>Account {{ i + 1 }}: {{ account.pageName }}</h4>
            <div class="token-field">
              <label>Instagram Account ID:</label>
              <input
                type="text"
                [value]="account.instagramAccountId"
                readonly
                (click)="
                  copyToClipboard(
                    account.instagramAccountId,
                    'Instagram Account ID'
                  )
                "
              />
            </div>
            <div class="token-field">
              <label>Page Access Token:</label>
              <input
                type="password"
                [value]="account.pageAccessToken"
                readonly
                (click)="
                  copyToClipboard(account.pageAccessToken, 'Access Token')
                "
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Firebase Function Example -->
      <div *ngIf="instagramAccounts.length > 0" class="code-example">
        <h3>📝 How to Use in Your Firebase Function</h3>
        <pre class="code-block">{{ getFirebaseExample() }}</pre>
      </div>
    </div>
  `,
  styles: [
    `
      .instagram-auth-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
      }

      .status {
        padding: 15px;
        margin: 15px 0;
        border-radius: 5px;
        font-weight: bold;
      }

      .status.success {
        background: #d4edda;
        color: #155724;
        border: 1px solid #c3e6cb;
      }
      .status.error {
        background: #f8d7da;
        color: #721c24;
        border: 1px solid #f5c6cb;
      }
      .status.info {
        background: #d1ecf1;
        color: #0c5460;
        border: 1px solid #bee5eb;
      }

      .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        margin: 5px;
        transition: all 0.3s;
      }

      .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .btn-primary {
        background: #4267b2;
        color: white;
      }
      .btn-primary:hover:not(:disabled) {
        background: #365899;
      }

      .btn-secondary {
        background: #6c757d;
        color: white;
      }
      .btn-secondary:hover:not(:disabled) {
        background: #5a6268;
      }

      .btn-success {
        background: #28a745;
        color: white;
      }
      .btn-success:hover:not(:disabled) {
        background: #218838;
      }

      .btn-outline {
        background: white;
        color: #007bff;
        border: 1px solid #007bff;
      }
      .btn-outline:hover:not(:disabled) {
        background: #007bff;
        color: white;
      }

      .info-card,
      .account-card,
      .token-card {
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 15px;
        margin: 10px 0;
        background: #f8f9fa;
      }

      .account-info h4 {
        margin: 0 0 10px 0;
        color: #333;
      }

      .test-result {
        margin-top: 10px;
        padding: 8px;
        border-radius: 4px;
      }

      .test-result.success {
        background: #d4edda;
        color: #155724;
      }
      .test-result.error {
        background: #f8d7da;
        color: #721c24;
      }

      .tokens-section {
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 8px;
        padding: 20px;
        margin: 20px 0;
      }

      .warning {
        background: #f0ad4e;
        color: white;
        padding: 10px;
        border-radius: 5px;
        margin-bottom: 15px;
      }

      .token-field {
        margin: 10px 0;
      }

      .token-field label {
        display: block;
        font-weight: bold;
        margin-bottom: 5px;
      }

      .token-field input {
        width: 100%;
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-family: monospace;
        cursor: pointer;
      }

      .code-example {
        margin-top: 30px;
        background: #f8f9fa;
        border-radius: 8px;
        padding: 20px;
      }

      .code-block {
        background: #2d3748;
        color: #e2e8f0;
        padding: 15px;
        border-radius: 5px;
        overflow-x: auto;
        font-size: 12px;
        line-height: 1.4;
      }
    `,
  ],
})
export class InstagramAuthComponent implements OnInit, OnDestroy {
  authStatus: AuthStatus = { isLoggedIn: false };
  instagramAccounts: InstagramAccount[] = [];
  isLoading = false;
  isLoadingInstagram = false;
  testingIndex: number | null = null;
  testResults: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(private instagramAuthService: InstagramAuthService) {}

  ngOnInit() {
    // Subscribe to auth status changes
    this.instagramAuthService.authStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.authStatus = status;
        this.isLoading = false;
      });

    // Subscribe to Instagram accounts changes
    this.instagramAuthService.instagramAccounts$
      .pipe(takeUntil(this.destroy$))
      .subscribe((accounts) => {
        this.instagramAccounts = accounts;
        this.testResults = new Array(accounts.length);
        this.isLoadingInstagram = false;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async login() {
    this.isLoading = true;
    try {
      await this.instagramAuthService.login();
    } catch (error) {
      console.error("Login error:", error);
      this.isLoading = false;
    }
  }

  async logout() {
    await this.instagramAuthService.logout();
  }

  async getInstagramAccounts() {
    this.isLoadingInstagram = true;
    try {
      await this.instagramAuthService.getInstagramAccounts();
    } catch (error) {
      console.error("Error getting Instagram accounts:", error);
      this.isLoadingInstagram = false;
    }
  }

  async testConnection(account: InstagramAccount, index: number) {
    this.testingIndex = index;
    try {
      const result = await this.instagramAuthService.testInstagramConnection(
        account
      );
      this.testResults[index] = { success: true, data: result };
    } catch (error: any) {
      this.testResults[index] = { success: false, error: error.message };
    } finally {
      this.testingIndex = null;
    }
  }

  copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log(`${label} copied to clipboard!`);
      alert(`${label} copied to clipboard!`);
    });
  }

  getStatusClass(): string {
    if (this.authStatus.isLoggedIn) return "success";
    if (this.authStatus.error) return "error";
    return "info";
  }

  getStatusMessage(): string {
    if (this.authStatus.isLoggedIn) {
      return `✅ Connected as ${this.authStatus.user?.name || "User"}`;
    }
    if (this.authStatus.error) {
      return `❌ ${this.authStatus.error}`;
    }
    return "🔐 Ready to connect to Facebook";
  }

  getFirebaseExample(): string {
    if (this.instagramAccounts.length === 0) return "";

    const account = this.instagramAccounts[0];
    return `// Call your Firebase function
const uploadReel = httpsCallable(functions, 'uploadInstagramReel');

const result = await uploadReel({
  videoUrl: 'https://your-domain.com/video.mp4',
  caption: 'Check out this amazing reel! #firebase',
  accessToken: '${account.pageAccessToken}',
  instagramAccountId: '${account.instagramAccountId}'
});`;
  }
}
